/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 */

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

function ensureTopbarToggle() {
  const topbar = document.querySelector('.topbar');
  const inner = topbar?.querySelector('.topbar__inner');
  if (!topbar || !inner) return;

  let button = topbar.querySelector('[data-topbar-toggle]');
  if (!button) {
    button = document.createElement('button');
    button.className = 'topbar__mobile-toggle';
    button.type = 'button';
    button.setAttribute('aria-label', 'Toggle announcement');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('data-topbar-toggle', '');
    button.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 9.5 12 14.5 17 9.5" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    inner.insertBefore(button, inner.firstChild);
  }

  button.setAttribute('aria-expanded', String(topbar.classList.contains('is-open')));
}

function bindTopbarToggle() {
  if (document.documentElement.dataset.irtmeTopbarDelegated === 'true') return;
  document.documentElement.dataset.irtmeTopbarDelegated = 'true';

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-topbar-toggle]') : null;
    if (!button) return;

    const topbar = button.closest('.topbar');
    if (!topbar) return;

    event.preventDefault();
    const open = topbar.classList.toggle('is-open');
    button.setAttribute('aria-expanded', String(open));
  });
}

function ensurePopularProductSnap() {
  const sections = document.querySelectorAll('[id*="popular_products"]');
  if (!sections.length) return;

  sections.forEach((section) => {
    const slides = section.querySelector('slideshow-slides');
    if (!slides || slides.dataset.irtmeSnapBound === 'true') return;

    slides.dataset.irtmeSnapBound = 'true';

    const snapToNearestCard = () => {
      const items = Array.from(slides.querySelectorAll('.resource-list__slide'));
      if (!items.length) return;

      const current = slides.scrollLeft;
      const nearest = items.reduce((best, item) => {
        const distance = Math.abs(item.offsetLeft - current);
        if (!best || distance < best.distance) return { item, distance };
        return best;
      }, null);

      if (!nearest || Math.abs(nearest.item.offsetLeft - current) <= 2) return;
      slides.scrollTo({ left: nearest.item.offsetLeft, behavior: 'auto' });
    };

    let scrollTimer = 0;
    slides.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(snapToNearestCard, 140);
      },
      { passive: true }
    );

    section.querySelectorAll('.slideshow-control, slideshow-arrows button').forEach((button) => {
      button.addEventListener('click', () => {
        window.setTimeout(snapToNearestCard, 500);
        window.setTimeout(snapToNearestCard, 950);
      });
    });

    slides.addEventListener('pointerup', () => window.setTimeout(snapToNearestCard, 120), { passive: true });
    slides.addEventListener('touchend', () => window.setTimeout(snapToNearestCard, 120), { passive: true });
  });
}

function ensureHeroShowcaseSwipe() {
  document.querySelectorAll('hero-carousel.hero-showcase__carousel').forEach((carousel) => {
    if (carousel.dataset.irtmeSwipeBound === 'true') return;
    carousel.dataset.irtmeSwipeBound = 'true';

    let startX = 0;
    let startY = 0;
    let dragging = false;
    let lastSwipeAt = 0;

    const slides = () => Array.from(carousel.querySelectorAll('[data-slide]'));
    const activeIndex = () => Math.max(0, slides().findIndex((slide) => slide.classList.contains('is-active')));
    const go = (next) => {
      const items = slides();
      if (!items.length) return;
      const index = ((next % items.length) + items.length) % items.length;
      if (typeof carousel.go === 'function') {
        carousel.go(index);
        return;
      }
      items.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === index));
      carousel.querySelectorAll('[data-dot]').forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
    };
    const bindArrow = (selector, direction) => {
      const button = carousel.querySelector(selector);
      if (!button || button.dataset.irtmeHeroClickBound === 'true') return;
      button.dataset.irtmeHeroClickBound = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        go(activeIndex() + direction);
      });
    };
    bindArrow('[data-prev]', -1);
    bindArrow('[data-next]', 1);

    const handleSwipe = (deltaX, deltaY) => {
      if (Math.abs(deltaY) > Math.abs(deltaX) * 1.2) return;
      const threshold = Math.max(36, carousel.offsetWidth * 0.11);
      if (Math.abs(deltaX) < threshold) return;
      const now = Date.now();
      if (now - lastSwipeAt < 220) return;
      lastSwipeAt = now;
      go(activeIndex() + (deltaX < 0 ? 1 : -1));
    };

    carousel.addEventListener(
      'pointerdown',
      (event) => {
        if (event.target.closest('a, button')) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
        startY = event.clientY;
        dragging = true;
      },
      true
    );
    window.addEventListener('pointerup', (event) => {
      if (!dragging) return;
      dragging = false;
      handleSwipe(event.clientX - startX, event.clientY - startY);
    });
    window.addEventListener('pointercancel', () => {
      dragging = false;
    });
    carousel.addEventListener(
      'touchstart',
      (event) => {
        if (event.target.closest('a, button')) return;
        const touch = event.changedTouches[0];
        if (!touch) return;
        startX = touch.clientX;
        startY = touch.clientY;
      },
      { passive: true, capture: true }
    );
    carousel.addEventListener(
      'touchend',
      (event) => {
        const touch = event.changedTouches[0];
        if (!touch) return;
        handleSwipe(touch.clientX - startX, touch.clientY - startY);
      },
      true
    );
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      ensureTopbarToggle();
      bindTopbarToggle();
      ensurePopularProductSnap();
      ensureHeroShowcaseSwipe();
    },
    { once: true }
  );
} else {
  ensureTopbarToggle();
  bindTopbarToggle();
  ensurePopularProductSnap();
  ensureHeroShowcaseSwipe();
}
